// THE PAGE KIT — the React side of attest.css §7, the one vocabulary every
// operator page dresses in. Eleven tabs were built by eleven hands; this file
// and that CSS section are where the shared machinery is registered ONCE:
//
//   PageShell   the page container — same max-width, same padding to the
//               topbar and sidebar on every tab.
//   PageHeader  the masthead — one h1 size/weight, one sub-line, the verbs
//               slot, closed by the page's single 2px ink rule.
//   Zone        a section in the page rhythm (30px, scroll-margin).
//   ZoneHead    the 12px tracked label on a 1px rule, with count / note /
//               route slots.
//   Rail        the instrument: a five-cell readout ON A WHITE SHEET riding
//               the masthead rule (the operator's design law: content sits on
//               sheets against the warm field; nothing floats).
//   RailCell    one cell — label row (+ optional go hint), figure (+ unit),
//               detail line. Button when it routes/filters, div when static.
//   Sheet       the standard bordered white container.
//   RowMenu     the row overflow — the destructive-cascade ⋯. Portaled to
//               <body> and positioned in JS off the trigger's own rect, so it
//               is never cropped by a `.sheet--clip` ancestor and flips above
//               the trigger when the viewport has no room below it.
//
// Pages add their own content classes INSIDE these; they do not re-invent the
// dress. If a page needs a variant, it goes here or in attest.css §7 — never
// in a per-page <style> block.

import {
  useEffect, useLayoutEffect, useRef, useState,
  type CSSProperties, type HTMLAttributes, type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
// the shared German vocabulary (verbs, statuses, priorities, phases…) — PageKit is in every
// bundle, so registering it here means every page has it without importing it itself
import '../i18n/common.de.js';
import '../i18n/common.fr.js';

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

// ── the shell ────────────────────────────────────────────────────────────────
export function PageShell({
  className, children, ...rest
}: { className?: string; children: ReactNode } & Omit<HTMLAttributes<HTMLDivElement>, 'className' | 'children'>) {
  return <div className={cx('page', className)} {...rest}>{children}</div>;
}

// ── the masthead ─────────────────────────────────────────────────────────────
export function PageHeader({
  title, sub, verbs, kicker, children, className,
}: {
  title: ReactNode;
  /** the meta line under the title — compose with <Sep /> between facts */
  sub?: ReactNode;
  /** right-hand controls, bottom-aligned with the title */
  verbs?: ReactNode;
  /** optional 12px tracked label ABOVE the title (e.g. Contracting's engagement code) */
  kicker?: ReactNode;
  /** extra masthead rows (rendered inside the rule) */
  children?: ReactNode;
  className?: string;
}) {
  return (
    <header className={cx('mast', className)}>
      <div className="mast-top">
        <div className="mast-main">
          {kicker != null && <div className="u-label">{kicker}</div>}
          <h1>{title}</h1>
          {sub != null && <div className="mast-sub">{sub}</div>}
        </div>
        {verbs != null && <div className="mast-verbs">{verbs}</div>}
      </div>
      {children}
    </header>
  );
}

/** the quiet separator dot used between masthead / zone-head facts */
export function Sep() {
  return <span className="sep">·</span>;
}

// ── zones ────────────────────────────────────────────────────────────────────
export function Zone({
  id, className, children, ...rest
}: { id?: string; className?: string; children: ReactNode } & Omit<HTMLAttributes<HTMLElement>, 'className' | 'children' | 'id'>) {
  return <section className={cx('zone', className)} id={id} {...rest}>{children}</section>;
}

export function ZoneHead({
  label, count, note, endNote, route, children, className,
}: {
  label: ReactNode;
  /** a tally beside the label (12.5px, ink-3, tabular) */
  count?: ReactNode;
  /** a sentence beside the label */
  note?: ReactNode;
  /** a note pushed to the right edge */
  endNote?: ReactNode;
  /** the zone's route out — an accent text verb at the right edge */
  route?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx('zone-head', className)}>
      <span className="u-label">{label}</span>
      {count != null && <span className="zone-count">{count}</span>}
      {note != null && <span className="zone-note">{note}</span>}
      {children}
      {endNote != null && <span className="zone-note zone-note--end">{endNote}</span>}
      {route != null && route}
    </div>
  );
}

// ── the instrument rail ──────────────────────────────────────────────────────
export function Rail({
  className, railClassName, children, after,
}: {
  className?: string;
  railClassName?: string;
  /** RailCells (or hand-rolled .fld cells for exotic pages) */
  children: ReactNode;
  /** extra bands inside the same sheet (filter bar, master switch) — use .instr-band */
  after?: ReactNode;
}) {
  return (
    <div className={cx('instr', className)}>
      <div className={cx('rail', railClassName)}>{children}</div>
      {after}
    </div>
  );
}

export type RailTone = 'ink' | 'ok' | 'warn' | 'attend' | 'err' | 'broken' | 'nil' | 'unknown';

export function RailCell({
  label, go, value, unit, tone, detail, onClick, pressed, disabled, title, valueTitle, className, children,
}: {
  label: ReactNode;
  /** the hover hint on the label row's right edge ('open', 'filter', '→ …') */
  go?: ReactNode;
  value: ReactNode;
  unit?: ReactNode;
  tone?: RailTone;
  detail?: ReactNode;
  onClick?: () => void;
  /** for cells that FILTER rather than route — stays pressed */
  pressed?: boolean;
  disabled?: boolean;
  title?: string;
  valueTitle?: string;
  className?: string;
  /** extra lines under the detail (e.g. Register's route line, Proposals' meter) */
  children?: ReactNode;
}) {
  const inner = (
    <>
      <span className="fl u-label">{label}{go != null && <span className="go" aria-hidden="true">{go}</span>}</span>
      <span
        className={cx('fv', 'voice-figure', tone && tone !== 'ink' && `fv--${tone}`)}
        title={valueTitle}
      >
        {value}
        {unit != null && <span className="u">{unit}</span>}
      </span>
      <span className="fd">{detail ?? ' '}</span>
      {children}
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        className={cx('fld', className)}
        onClick={onClick}
        aria-pressed={pressed}
        disabled={disabled}
        title={title}
      >
        {inner}
      </button>
    );
  }
  return <div className={cx('fld', className)} title={title}>{inner}</div>;
}

// ── the sheet ────────────────────────────────────────────────────────────────
export function Sheet({
  className, clip, strong, edge, pad, style, children, ...rest
}: {
  className?: string;
  /** overflow hidden — needed when inner rows must not escape the radius */
  clip?: boolean;
  strong?: boolean;
  /** the zone's one loud card: 2px ink left edge */
  edge?: boolean;
  pad?: boolean;
  style?: CSSProperties;
  children: ReactNode;
} & Omit<HTMLAttributes<HTMLDivElement>, 'className' | 'style' | 'children'>) {
  return (
    <div
      className={cx('sheet', clip && 'sheet--clip', strong && 'sheet--strong', edge && 'sheet--edge', pad && 'sheet--pad', className)}
      style={style}
      {...rest}
    >
      {children}
    </div>
  );
}

// ── the row overflow (attest.css §7 .rowmenu) ──────────────────────────────
//
// A `.sheet--clip` band deliberately clips its rows to the sheet's own
// radius — that clip must not also crop the menu a row's ⋯ opens. Portaling
// to <body> and positioning the menu in JS off the trigger's own
// getBoundingClientRect() is what escapes that ancestor honestly, rather
// than punching a hole in the clip or guessing a fixed direction that only
// happens to clear the fold for whichever row someone tested.

/** Controlled — the caller owns `open` so only one row's menu is ever up at once, if it wants that. */
export function RowMenu({
  label, open, onOpenChange, children, trigger, triggerClassName,
}: {
  /** the trigger's title / aria label, e.g. `More for Avanti` */
  label: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** the menu's own buttons/links */
  children: ReactNode;
  /** what the trigger shows — default the quiet ⋯; the register's confirmed mark passes a ✓ */
  trigger?: ReactNode;
  /** the trigger's classes — default `btn btn-sm btn-ghost` */
  triggerClassName?: string;
}) {
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // Re-measured against the trigger's live rect every time the menu opens — never a fixed
  // CSS direction, so a row hard against the window's lower edge flips the menu upward instead
  // of rendering it partly off-screen.
  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    const btn = btnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const menuH = menuRef.current?.offsetHeight ?? 0;
    const menuW = menuRef.current?.offsetWidth ?? 210;
    const gap = 5;
    const margin = 8;
    const fitsBelow = r.bottom + gap + menuH <= window.innerHeight - margin;
    const top = fitsBelow ? r.bottom + gap : Math.max(margin, r.top - gap - menuH);
    const left = Math.min(Math.max(margin, r.right - menuW), window.innerWidth - menuW - margin);
    setPos({ top, left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = () => onOpenChange(false);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onOpenChange]);

  return (
    <span className="rowmenu-wrap" onClick={(e) => e.stopPropagation()}>
      <button
        ref={btnRef}
        type="button"
        className={triggerClassName ?? 'btn btn-sm btn-ghost'}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => onOpenChange(!open)}
      >{trigger ?? '⋯'}</button>
      {open && createPortal(
        <div
          ref={menuRef}
          className="rowmenu"
          role="menu"
          // Hidden until the trigger's rect has been measured (first paint), so it never
          // flashes at the portal's default (0, 0) before snapping to place.
          style={pos ? { top: pos.top, left: pos.left } : { top: 0, left: 0, visibility: 'hidden' }}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>,
        document.body,
      )}
    </span>
  );
}
